import type { ReactNode } from "react";
import { Badge, Button, Spinner, type PortalActivityItem } from "@dearbill/shared";
import { fmtRelativeDay } from "../dashboardFormat";
import { usePortalFeed } from "../hooks/usePortalFeed";
import { HomeCard } from "./HomeCard";

/** Member-facing channel-badge labels; an unknown channel shows as sent. */
const CHANNEL_LABELS: Record<string, string> = {
  sms: "Text",
  email: "Email",
  voice: "Phone",
  chat: "Chat",
  portal: "Portal",
};

interface TimelineCardProps {
  /** Portal session bearer token. */
  session: string;
  /** Called when the session is rejected (401). */
  onSignOut: () => void;
}

/**
 * Activity rows carry no id; the timestamp, kind and summary together tell
 * two rows apart for the refresh merge.
 */
function activityKey(item: PortalActivityItem): string {
  return `${item.at}|${item.kind}|${item.summary}`;
}

/**
 * The dashboard Timeline: newest-first pages of `GET /api/portal/activity`.
 * "Show more" appends the next page through the opaque `nextCursor`; the
 * button disappears when the log is exhausted. A load failure shows a retry
 * line rather than hiding what already loaded. New events appear without a
 * reload: `usePortalFeed` refreshes page one on focus and on an interval.
 */
export function TimelineCard({ session, onSignOut }: TimelineCardProps): ReactNode {
  const { items, hasMore, loadingMore, error, loadMore, retry } = usePortalFeed(
    "/api/portal/activity",
    session,
    onSignOut,
    activityKey,
  );

  let body: ReactNode;
  if (items === null && !error) {
    body = (
      <div className="text-slate-teal flex items-center gap-3">
        <Spinner /> <span>Loading your timeline…</span>
      </div>
    );
  } else if (items === null) {
    body = (
      <p className="text-body-sm text-slate-teal">
        We couldn’t load your timeline.{" "}
        <button
          type="button"
          onClick={retry}
          className="text-primary-text cursor-pointer font-semibold underline hover:no-underline"
        >
          Try again
        </button>
      </p>
    );
  } else if (items.length === 0) {
    body = (
      <p className="text-body-sm text-slate-teal">
        Nothing here yet — as we work on your enrollment, every step will show up here.
      </p>
    );
  } else {
    body = (
      <>
        <ul className="flex flex-col">
          {items.map((item, i) => (
            <li
              key={`${item.at}-${i}`}
              className="border-haze flex items-start justify-between gap-4 border-b py-4 first:pt-0 last:border-b-0 last:pb-0"
            >
              <div className="flex min-w-0 flex-col gap-1">
                <p className="text-body-sm text-midnight font-medium">{item.summary}</p>
                <p className="text-caption text-slate-teal">{fmtRelativeDay(item.at)}</p>
              </div>
              {item.channel !== null && (
                <Badge tone="neutral" className="shrink-0">
                  {CHANNEL_LABELS[item.channel] ?? item.channel}
                </Badge>
              )}
            </li>
          ))}
        </ul>
        {hasMore && (
          <Button
            variant="secondary"
            size="md"
            className="self-start"
            disabled={loadingMore}
            onClick={loadMore}
          >
            {loadingMore ? "Loading…" : "Show more"}
          </Button>
        )}
        {error && (
          <p role="alert" className="text-body-sm text-danger">
            We couldn’t load more right now. Please try again.
          </p>
        )}
      </>
    );
  }

  return (
    <HomeCard title="Timeline" titleSize="lg">
      {body}
    </HomeCard>
  );
}
